/**
 * When a fixture sits relative to now, which decides whether a pool can still
 * be run on it.
 *
 * A squares board has to be sold and the numbers drawn before scoring starts,
 * so a finished game cannot host one at all. A game already under way is a
 * different case: deciding at 1:05 to run a pool on the 1:00 game is a real
 * thing people do, so it stays possible but is not offered by default.
 */

import type { Fixture } from './types';

export type FixtureTiming = 'upcoming' | 'started' | 'played';

export function fixtureTiming(fixture: Fixture, now: Date = new Date()): FixtureTiming {
  if (fixture.completed) return 'played';
  const kickoff = Date.parse(fixture.kickoffAt);
  if (!Number.isFinite(kickoff)) return 'upcoming';
  return kickoff <= now.getTime() ? 'started' : 'upcoming';
}

/** Nothing can be pooled on a game that is already over. */
export function canPoolOn(fixture: Fixture, now: Date = new Date()): boolean {
  return fixtureTiming(fixture, now) !== 'played';
}

export interface SplitFixtures {
  /** Offered by default: not yet kicked off, plus anything under way. */
  selectable: Fixture[];
  /** Finished. Kept only so the list reads as the right team's season. */
  played: Fixture[];
}

/**
 * Splits a schedule into what can still be pooled and what cannot.
 *
 * Both keep their original ordering, which is by kickoff.
 */
export function splitFixtures(
  fixtures: Fixture[],
  now: Date = new Date(),
): SplitFixtures {
  const selectable: Fixture[] = [];
  const played: Fixture[] = [];
  for (const fixture of fixtures) {
    (canPoolOn(fixture, now) ? selectable : played).push(fixture);
  }
  return { selectable, played };
}
